import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import collectionApiClient, {
  ApprovedCollection,
  DraftCollection,
} from "../../../api/collection";

// Callers may send only the fields they are changing; anything omitted is left
// as-is on the server, so a partial update can't clobber a field that changed
// elsewhere (e.g. the plan, which has its own endpoint).
export type CollectionUpdate<T> = Partial<Omit<T, "type">> & { id: string };

const useUpsertCollectionMutation = <
  T extends ApprovedCollection | DraftCollection,
>(
  updateFn: (
    collectionId: string,
    collection: Partial<Omit<T, "type">>,
  ) => Promise<void>,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["upsert-collection"],
    mutationFn: (collection: CollectionUpdate<T>) => {
      return updateFn(collection.id, {
        ...collection,
        klydos: undefined,
        creator: undefined,
      });
    },
    onMutate: async (collection: CollectionUpdate<T>) => {
      // Patch the cached collection rather than replacing it: `collection` may
      // be a partial update.
      const previousCollection = queryClient.getQueryData<T>([
        "collection",
        collection.id,
      ]);
      queryClient.setQueryData<T>(["collection", collection.id], (oldData) =>
        oldData ? { ...oldData, ...collection } : undefined,
      );

      const matchingQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: ["collections"] });

      // Snapshot every list the patch below touches, so onError can put them
      // back. The onSettled refetch is not enough on its own: it fails for the
      // same reasons the mutation did (offline, API down), which would leave
      // the list asserting an update that never landed.
      const previousLists = matchingQueries.map(({ queryKey }) => ({
        queryKey,
        data: queryClient.getQueryData<InfiniteData<T[]>>(queryKey),
      }));

      matchingQueries.forEach(({ queryKey }) => {
        queryClient.setQueryData<InfiniteData<T[]>>(queryKey, (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) =>
              page.map((oldCollection) =>
                oldCollection.id === collection.id
                  ? { ...oldCollection, ...collection }
                  : oldCollection,
              ),
            ),
          };
        });
      });

      return { previousCollection, previousLists };
    },
    onError: (_error, collection, context) => {
      // Only entries the patch actually wrote need restoring — it no-ops on a
      // cache miss, so an absent snapshot means nothing was touched.
      if (context?.previousCollection) {
        queryClient.setQueryData<T>(
          ["collection", collection.id],
          context.previousCollection,
        );
      }
      context?.previousLists.forEach(({ queryKey, data }) => {
        if (data) queryClient.setQueryData<InfiniteData<T[]>>(queryKey, data);
      });
    },
    // Also drop the single collection entry: on success the optimistic patch
    // misses server-side fields (e.g. the reviewStatus.updatedAt stamp that the
    // Studio notification dot reads).
    onSettled: (_data, _error, collection) =>
      Promise.all([
        queryClient.refetchQueries({ queryKey: ["collections"] }),
        queryClient.invalidateQueries({
          queryKey: ["collection", collection.id],
        }),
      ]),
  });
};

export const useUpdateDraftCollectionMutation = () => {
  return useUpsertCollectionMutation<DraftCollection>(
    collectionApiClient.upsertDraftCollection,
  );
};
export const useUpdateApprovedCollectionMutation = () => {
  return useUpsertCollectionMutation<ApprovedCollection>(
    collectionApiClient.upsertApprovedCollection,
  );
};
